import { useEffect, useRef, useState } from 'react';

const DEFAULT_URL = 'https://claude.ai/projects';

/** 외부 AI의 답처럼 보이는가: 앱 표식이 있거나, 보고서 형식(칸 제목 ### 이 3개 이상) */
export function looksLikeAiResult(text: string): boolean {
  if (/BEGIN_COUNSEL_AI_JSON/.test(text)) return true;
  if (/"formatVersion"\s*:/.test(text)) return true;
  return (text.match(/^#{3,4}\s/gm) ?? []).length >= 3;
}

/**
 * 외부로 보내기: 자료를 복사하고, 이 내담자의 외부 AI 프로젝트를 바로 연다 (주소가 없으면 프로젝트 목록).
 * 링크로 여는 이유: 휴대폰에서는 설치된 앱(Claude)이 바로 열린다.
 */
export function SendButton({
  text,
  projectUrl,
  label = '외부로 보내기',
  onSent,
}: {
  text: string;
  projectUrl?: string | undefined;
  label?: string;
  onSent?: () => void;
}) {
  const [done, setDone] = useState(false);
  return (
    <span className="row">
      <a
        className="button primary"
        href={projectUrl || DEFAULT_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          // 링크가 열리기 전에(같은 누름 안에서) 복사를 시작한다
          void navigator.clipboard
            ?.writeText(text)
            .then(() => {
              setDone(true);
              onSent?.();
            })
            .catch(() => setDone(false));
        }}
      >
        {label} ↗
      </a>
      <span className="muted small">
        {done ? '복사됨 — 열린 프로젝트의 입력창에 붙여넣고 보내세요' : `${Math.round(text.length / 1000)}천 자`}
        {!projectUrl && ' · 프로젝트 주소를 저장하면 그 프로젝트가 바로 열립니다'}
      </span>
    </span>
  );
}

/**
 * 외부 답 받기: 앱으로 돌아오면 클립보드에 답이 있는지 보고 "반영할까요?"를 묻는다 (자동 반영 안 함).
 * 클립보드를 못 읽는 기기(아이폰 등)는 [복사한 답 가져오기]를 누르면 된다. 직접 붙여넣는 칸도 남겨 둔다.
 */
export function ReceiveBox({
  title,
  hint,
  accept = looksLikeAiResult,
  onSubmit,
}: {
  title: string;
  hint: string;
  accept?: (text: string) => boolean;
  onSubmit: (raw: string) => Promise<unknown>;
}) {
  const [raw, setRaw] = useState('');
  const [found, setFound] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const handled = useRef<Set<string>>(new Set());

  useEffect(() => {
    const check = () => {
      if (document.visibilityState !== 'visible' || !navigator.clipboard?.readText) return;
      navigator.clipboard
        .readText()
        .then((t) => {
          if (t && accept(t) && !handled.current.has(t)) setFound(t);
        })
        .catch(() => undefined); // 권한이 없거나 지원하지 않으면 버튼으로
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [accept]);

  const submit = (text: string) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    handled.current.add(text);
    onSubmit(text)
      .then(() => {
        setRaw('');
        setFound(null);
        setMsg('가져왔습니다.');
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false));
  };

  const fromClipboard = () => {
    setErr(null);
    setMsg(null);
    if (!navigator.clipboard?.readText) {
      setErr('이 브라우저는 클립보드 읽기를 지원하지 않습니다. 아래 칸에 붙여넣으세요.');
      return;
    }
    navigator.clipboard
      .readText()
      .then((t) => {
        if (!t.trim()) setErr('클립보드가 비어 있습니다. 외부 AI 답을 복사했는지 확인하세요.');
        else if (!accept(t)) setErr('클립보드 내용이 외부 AI 답 형식이 아닙니다. 답 전체를 복사했는지 확인하세요.');
        else submit(t);
      })
      .catch(() => setErr('클립보드를 읽지 못했습니다 (권한). 아래 칸에 붙여넣으세요.'));
  };

  return (
    <div className="card stack receive-box">
      <h2>{title}</h2>
      {found && (
        <div className="found-banner row">
          <span>
            복사한 외부 답이 있습니다 <span className="muted small">({Math.round(found.length / 1000)}천 자)</span>
          </span>
          <button type="button" className="primary" disabled={busy} onClick={() => submit(found)}>
            {busy ? '가져오는 중…' : '이 답 가져오기'}
          </button>
          <button
            type="button"
            className="link"
            onClick={() => {
              handled.current.add(found);
              setFound(null);
            }}
          >
            아니요
          </button>
        </div>
      )}
      <div className="row">
        <button type="button" className={found ? undefined : 'primary'} disabled={busy} onClick={fromClipboard}>
          📋 복사한 답 가져오기
        </button>
        <span className="muted small">{hint}</span>
      </div>
      <details>
        <summary className="small">직접 붙여넣기</summary>
        <textarea aria-label={title} rows={6} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="여기에 붙여넣기" />
        <button type="button" disabled={!raw.trim() || busy} onClick={() => submit(raw)}>
          {busy ? '저장 중…' : '저장하고 읽기'}
        </button>
      </details>
      {msg && <p className="ok small">{msg}</p>}
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
    </div>
  );
}
